import React, { useState } from "react";
import { authAPI } from "../../services/api";
import LoadingSpinner from "../LoadingSpinner";
import ErrorMessage from "../ErrorMessage";

/**
 * Magic Link Authentication Component
 *
 * Handles passwordless authentication by sending a magic link to the user's email.
 * Supports both login (existing users) and registration (new users).
 */
const MagicLinkButton = ({
  mode = "login", // 'login' | 'register'
  onSuccess,
  onError,
  disabled = false,
  className = "",
  showEmailInput = true,
  initialEmail = "",
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);

    // Validate email
    if (!email || !email.trim()) {
      setError("Email address is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      // Request magic link from backend
      const response = await authAPI.requestMagicLink(email);

      setSubmitted(true);
      onSuccess?.(response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        "Failed to send magic link. Please try again.";

      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setSubmitted(false);
    setError(null);
    // Trigger form submission again
    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent);
  };

  const handleBackToForm = () => {
    setSubmitted(false);
    setError(null);
  };

  // Success state - show email sent message
  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Check your email
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            We sent a magic link to <strong>{email}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Click the link in the email to sign in. The link expires in 15 minutes.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={isLoading}
            className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : "Resend magic link"}
          </button>

          <button
            onClick={handleBackToForm}
            className="w-full inline-flex justify-center py-2 px-4 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  // Form state - show email input and submit button
  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`} noValidate>
      {error && (
        <ErrorMessage
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {showEmailInput && (
        <div>
          <label
            htmlFor="magic-link-email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email address
          </label>
          <input
            id="magic-link-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={handleEmailChange}
            disabled={disabled || isLoading}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Enter your email"
          />
        </div>
      )}

      <button
        type="submit"
        disabled={disabled || isLoading || !email}
        className="group relative w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      >
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <>
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span>Send Magic Link</span>
          </>
        )}
      </button>

      {mode === "login" && (
        <p className="text-xs text-center text-gray-500">
          We'll send you a secure link to sign in without a password.
        </p>
      )}
      {mode === "register" && (
        <p className="text-xs text-center text-gray-500">
          We'll send you a magic link to verify your email and complete registration.
        </p>
      )}
    </form>
  );
};

export default MagicLinkButton;
